import { Fragment, useState, useEffect } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";

const NAVY = "#0B2545";
const NAVY_DEEP = "#07182E";
const GOLD = "#C9A24B";
const PAPER = "#F4F1E8";
const FONT_SANS = "'IBM Plex Sans', system-ui, sans-serif";

type AnchorItem = { label: string; hash: string };

const DEFAULT_ANCHORS: AnchorItem[] = [
  { label: "Platform", hash: "how" },
  { label: "Pricing", hash: "pricing" },
  { label: "FAQ", hash: "faq" },
];

export function MobileNav({
  anchors = DEFAULT_ANCHORS,
  className,
  hideContact,
  signInLabel = "Sign in",
  ctaLabel = "Get started",
  onCtaClick,
}: {
  anchors?: AnchorItem[];
  className?: string;
  hideContact?: boolean;
  signInLabel?: string;
  ctaLabel?: string;
  onCtaClick?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const { session, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const isAuthed = Boolean(session);

  useEffect(() => {
    function onResize() {
      if (window.innerWidth >= 1024) setOpen(false);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className={className} style={{ display: "none" }}>
      <button
        type="button"
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 42,
          height: 42,
          background: "transparent",
          border: "1px solid rgba(237,234,224,.28)",
          borderRadius: 4,
          color: PAPER,
          cursor: "pointer",
          padding: 0,
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          {open ? (
            <>
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </>
          ) : (
            <>
              <line x1="4" y1="7" x2="20" y2="7" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="17" x2="20" y2="17" />
            </>
          )}
        </svg>
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: NAVY_DEEP,
            borderTop: "1px solid rgba(255,255,255,.08)",
            borderBottom: "1px solid rgba(255,255,255,.08)",
            padding: "12px 20px 20px",
            zIndex: 50,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            fontFamily: FONT_SANS,
            boxShadow: "0 24px 48px -24px rgba(0,0,0,.6)",
          }}
        >
          {anchors.map((a) => (
            <Link
              key={a.hash}
              to="/"
              hash={a.hash}
              onClick={() => setOpen(false)}
              style={{ color: "#C2CAD6", fontSize: 15, padding: "12px 4px", textDecoration: "none" }}
            >
              {a.label}
            </Link>
          ))}
          {!hideContact && (
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              style={{ color: "#C2CAD6", fontSize: 15, padding: "12px 4px", textDecoration: "none" }}
            >
              Contact
            </Link>
          )}
          {loading ? null : isAuthed ? (
            <>
              <div style={{ height: 1, background: "rgba(255,255,255,.08)", margin: "10px 0" }} />
              <Link
                to="/dashboard"
                onClick={() => setOpen(false)}
                style={{
                  marginTop: 4,
                  display: "inline-flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "12px 18px",
                  background: GOLD,
                  color: NAVY,
                  fontSize: 15,
                  fontWeight: 600,
                  borderRadius: 2,
                  textDecoration: "none",
                }}
              >
                Dashboard
              </Link>
              <button
                type="button"
                onClick={async () => {
                  setOpen(false);
                  await signOut();
                  navigate({ to: "/" });
                }}
                style={{
                  marginTop: 8,
                  background: "transparent",
                  border: "none",
                  textAlign: "left",
                  color: "#C2CAD6",
                  fontSize: 15,
                  padding: "12px 4px",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <div style={{ height: 1, background: "rgba(255,255,255,.08)", margin: "10px 0" }} />
              <Link
                to="/auth"
                search={{ mode: "signin" as const }}
                onClick={() => setOpen(false)}
                style={{ color: "#C2CAD6", fontSize: 15, padding: "12px 4px", textDecoration: "none" }}
              >
                {signInLabel}
              </Link>
              <Link
                to="/auth"
                search={{ mode: "signup" as const }}
                onClick={() => {
                  setOpen(false);
                  onCtaClick?.();
                }}
                style={{
                  marginTop: 8,
                  display: "inline-flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "12px 18px",
                  background: GOLD,
                  color: NAVY,
                  fontSize: 15,
                  fontWeight: 600,
                  borderRadius: 2,
                  textDecoration: "none",
                }}
              >
                {ctaLabel}
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}